import classNames from 'classnames/bind';
import { createSearchParams } from 'react-router-dom';
import { ROUTES } from '../../../../Base.constants';
import { ItemType, NavigationItem } from '../../../../models/Navigation';

import styles from '../Navigation.module.scss';

const cx = classNames.bind(styles);

const hasMarketPermissions = (permissions, ...required) => {
  required.push('content:research:all');

  for (const item of required) {
    if (permissions.indexOf(item) !== -1) {
      return true;
    }
  }

  return false;
};

const getNavLink = (text: string, to: string): NavigationItem => ({
  type: ItemType.NAVLINK,
  text,
  to,
  className: cx('auto-width'),
});

export const marketsDropdownOptions = (permissions: string[] | undefined): NavigationItem[] => {
  if (!permissions) {
    return [];
  }

  const options: NavigationItem[] = [];

  const northAmericaOptions = getNorthAmericaDropdownOptions(permissions);
  if (northAmericaOptions.length) {
    options.push({
      type: ItemType.DROPDOWN,
      text: 'North America',
      options: northAmericaOptions,
    });
  }

  const europeOptions = getEuropeDropdownOptions(permissions);
  if (europeOptions.length) {
    options.push({
      type: ItemType.DROPDOWN,
      text: 'Europe',
      options: europeOptions,
    });
  }

  if (hasMarketPermissions(permissions, 'content:research:asia_pacific')) {
    options.push(getNavLink('Asia Pacific', `${ROUTES.globalFeed}?${createSearchParams({ region: ['region/832'] })}`));
  }

  if (
    hasMarketPermissions(
      permissions,
      'content:research:emerging_markets:hy',
      'content:research:emerging_markets:ig',
      'content:research:emerging_markets:crossover'
    )
  ) {
    options.push(
      getNavLink('Emerging Markets', `${ROUTES.globalFeed}?${createSearchParams({ region: ['region/156'] })}`)
    );
  }
  if (hasMarketPermissions(permissions, 'content:research:strategy')) {
    options.push(getNavLink('Strategy', `${ROUTES.globalFeed}?${createSearchParams({ topic: ['tag/strategy'] })}`));
  }

  return options;
};

const getNorthAmericaDropdownOptions = (permissions: string[]): NavigationItem[] => {
  const options: NavigationItem[] = [];

  if (hasMarketPermissions(permissions, 'content:research:us', 'content:research:us:ig')) {
    options.push(
      getNavLink(
        'Investment Grade',
        `${ROUTES.globalFeed}?${createSearchParams({
          region: ['region/21'],
          creditGrade: ['tag/investment_grade'],
        })}`
      )
    );
  }
  if (hasMarketPermissions(permissions, 'content:research:us', 'content:research:us:hy')) {
    options.push(
      getNavLink(
        'High Yield',
        `${ROUTES.globalFeed}?${createSearchParams({
          region: ['region/21'],
          creditGrade: ['tag/high_yield'],
        })}`
      )
    );
  }
  if (hasMarketPermissions(permissions, 'content:research:us', 'content:research:us:hy')) {
    options.push(
      getNavLink(
        'Loans',
        `${ROUTES.globalFeed}?${createSearchParams({
          region: ['region/21'],
          creditGrade: ['tag/high_yield'],
          assetClass: ['tag/loans'],
        })}`
      )
    );
  }
  if (hasMarketPermissions(permissions, 'content:research:us')) {
    options.push(
      getNavLink(
        'Structured Finance',
        `${ROUTES.globalFeed}?${createSearchParams({
          region: ['region/21'],
          assetClass: ['tag/structured_finance'],
        })}`
      )
    );
  }
  if (hasMarketPermissions(permissions, 'content:research:municipals', 'content:research:us')) {
    options.push(getNavLink('Municipals', `${ROUTES.globalFeed}?${createSearchParams({ topic: ['tag/municipals'] })}`));
  }
  return options;
};

const getEuropeDropdownOptions = (permissions: string[]): NavigationItem[] => {
  const options: NavigationItem[] = [];

  if (hasMarketPermissions(permissions, 'content:research:europe', 'content:research:europe:ig')) {
    options.push(
      getNavLink(
        'Investment Grade',
        `${ROUTES.globalFeed}?${createSearchParams({
          region: ['region/150'],
          creditGrade: ['tag/investment_grade'],
        })}`
      )
    );
  }
  if (hasMarketPermissions(permissions, 'content:research:europe', 'content:research:europe:hy')) {
    options.push(
      getNavLink(
        'High Yield',
        `${ROUTES.globalFeed}?${createSearchParams({
          region: ['region/150'],
          creditGrade: ['tag/high_yield'],
        })}`
      )
    );
  }
  if (hasMarketPermissions(permissions, 'content:research:europe', 'content:research:europe:hy')) {
    options.push(
      getNavLink(
        'Loans',
        `${ROUTES.globalFeed}?${createSearchParams({
          region: ['region/150'],
          creditGrade: ['tag/high_yield'],
          assetClass: ['tag/loans'],
        })}`
      )
    );
  }
  if (hasMarketPermissions(permissions, 'content:research:europe')) {
    options.push(
      getNavLink(
        'Structured Finance',
        `${ROUTES.globalFeed}?${createSearchParams({
          region: ['region/150'],
          assetClass: ['tag/structured_finance'],
        })}`
      )
    );
  }
  return options;
};
